import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Read, Result, TaskBox } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { refusedRead } from "./course-page-boxes";
import { calendarSubscriptionApi, type CalendarSubscriptionState } from "@/lib/api/calendarSubscription";
import { checkCommit, type CommitCheck } from "@/lib/api/commitSeams";
import { subscriptionWord, CALENDAR_COPY } from "./calendar-subscription-words";
import { AddressPart, ChoicePart } from "./CalendarSubscriptionParts";

/** What a refusal says. A 404 from the route means this seat holds no place on the course, never "not found". */
function refusalWords(error: unknown): string {
  if (error instanceof ApiError) return error.status === 404 ? CALENDAR_COPY.seat : error.message;
  return CALENDAR_COPY.failed;
}

const calendarSubscriptionKey = (courseId: string) => ["calendar-subscription", courseId] as const;

export type Act = "create" | "replace" | "stop";

export function useCalendarBox(courseId: string): TaskBox<CalendarSubscriptionState> {
  const copy = CALENDAR_COPY;
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: calendarSubscriptionKey(courseId),
    queryFn: () => calendarSubscriptionApi.get(courseId),
    retry: false,
    staleTime: 0,
  });
  const [address, setAddress] = useState<string | null>(null);
  const [choice, setChoice] = useState<Act | null>(null);
  const [result, setResult] = useState<Result>({ state: "none" });

  const readAt = new Date(status.dataUpdatedAt).toISOString();
  // A seat the course refuses holds no place on it, even after an earlier read: the box isn't drawn (R1).
  // A transient fault keeps the last state, marked stale (R2).
  const read: Read<CalendarSubscriptionState> = refusedRead(status.error)
    ? { status: "forbidden" }
    : status.data
      ? status.isError
        ? { status: "stale", last: status.data, readAt, sentence: copy.stale, refresh: () => void status.refetch() }
        : { status: "ready", value: status.data, readAt }
      : status.isError
        ? { status: "failed", sentence: copy.failed, retry: () => void status.refetch() }
        : { status: "loading" };
  const active = status.data?.state === "active";
  const act: Act = active ? (choice ?? "replace") : "create";

  const commitAct = async (doing: Act, requestId: string): Promise<Result> => {
    const done = (address: boolean): Result => ({
      state: "confirmed",
      at: new Date().toISOString(),
      effects: [copy.done[doing]],
      // A replayed or checked mint never shows its address again (W1).
      remains: doing === "stop" || address ? [] : [copy.lostAddress],
    });
    // The same key is the receipt: Check reads it, and never sends the command again (W1).
    const unconfirmed: Result = {
      state: "unknown",
      cap: copy.unknown,
      requestId,
      sentence: copy.unknownSentence,
      check: {
        verb: copy.check,
        note: copy.checkNote,
        run: async () => {
          let receipt: CommitCheck;
          try {
            receipt = await checkCommit(requestId);
          } catch {
            // A failed Check proves nothing either way.
            return unconfirmed;
          }
          const checked: Result =
            receipt.state === "confirmed" || receipt.state === "partial" ? done(false) : receipt.state === "not_committed" ? { state: "none" } : unconfirmed;
          if (checked.state === "confirmed") {
            setChoice(null);
            await queryClient.invalidateQueries({ queryKey: calendarSubscriptionKey(courseId) });
          }
          setResult(checked);
          return checked;
        },
      },
    };
    let next: Result;
    try {
      const answer = doing === "stop" ? await calendarSubscriptionApi.stop(courseId, requestId) : await calendarSubscriptionApi.mint(courseId, requestId);
      const { address: minted, ...state } = answer as CalendarSubscriptionState & { address?: string | null };
      queryClient.setQueryData(calendarSubscriptionKey(courseId), state);
      setAddress(minted ?? null);
      setChoice(null);
      next = done(Boolean(minted));
    } catch (error) {
      next = error instanceof ApiError && error.status >= 400 && error.status < 500 ? { state: "refused", cap: copy.refused, sentence: refusalWords(error), draft: "kept" } : unconfirmed;
    }
    setResult(next);
    return next;
  };

  return {
    kind: "task",
    id: `calendar-${courseId}`,
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    // The address is shown once, so the Task holds it open, under its result, until the page is left.
    fold: address ? "never" : "choice",
    read,
    state: (value) => ({ class: value.state === "active" ? "done" : "quiet", word: subscriptionWord(value) }),
    detail: () => copy.detail,
    sub: () => copy.updates,
    act: { verb: active ? copy.manage : copy.add, does: "open", opens: address ? "address" : active ? "choice" : "address" },
    // An active address rests on its word; this reopens it to replace or stop it.
    exits: active && !address ? [{ label: copy.manage, reopens: true }] : undefined,
    parts: address
      ? [{ id: "address", label: copy.address, state: { class: "done", word: copy.shownOnce }, markedByBox: true, owner: { system: "imam" }, fold: "never", draft: "none", render: () => <AddressPart address={address} /> }]
      : active
        ? [{ id: "choice", label: copy.choice, state: { class: choice ? "draft" : "quiet", word: subscriptionWord(status.data) }, owner: { system: "imam" }, fold: "never", draft: "browser", summary: copy.lost, render: () => <ChoicePart choice={choice} onChoose={setChoice} /> }]
        : [{ id: "address", label: copy.address, state: { class: "quiet", word: subscriptionWord(status.data) }, markedByBox: true, owner: { system: "imam" }, fold: "never", draft: "none", summary: copy.toCreate }],
    commit: {
      verb: copy.verbs[act],
      cap: copy.caps[act],
      effects: () => copy.effects[act].map((sentence, index) => ({ id: `${act}-${index}`, sentence })),
      blockers: () => (active && !address && choice === null ? [{ kind: "missing", part: "choice", sentence: copy.choose }] : []),
      run: (requestId) => commitAct(act, requestId),
    },
    result,
    draft: choice !== null,
  };
}
